import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import FolderCopyOutlinedIcon from '@mui/icons-material/FolderCopyOutlined';
import FolderSharedOutlinedIcon from '@mui/icons-material/FolderSharedOutlined';
import { AppTabs } from '@client/foundation/design-system/components';
import { SharedResourcesGrid } from './shared-resources-grid';
import type { TemplateType } from '@server/trading-templates/schemas/input.schema';
import type { SharedResourceType } from '@server/shared-resources/schemas';

type SharedResourceTabsProps = {
  sharedResourceType: SharedResourceType;
  sharedTemplateType?: TemplateType;
  renderMine: () => React.ReactNode;
  onImportShared: (
    sharedResourceId: string,
    sharedResourceName: string
  ) => void;
  onModifyShared?: (
    sharedResourceId: string,
    sharedResourceName: string,
    sharedResourceNotes?: string
  ) => void;
  onRevokeShared?: (sharedResourceId: string) => void;
};

type SharedResourcesTab = 'mine' | 'shared';

const SharedTabLabel = ({
  title,
  icon
}: {
  title: string;
  icon: React.ReactNode;
}) => (
  <Box className="app-tabs-label">
    <Box component="span" data-tab-title>
      <Box component="span" className="app-tabs-icon-shell">
        {icon}
      </Box>
      <Typography component="span" variant="body2" fontWeight={700}>
        {title}
      </Typography>
    </Box>
  </Box>
);

const sharedResourceTabs = [
  {
    value: 'mine',
    label: (
      <SharedTabLabel title="Mine" icon={<FolderCopyOutlinedIcon />} />
    )
  },
  {
    value: 'shared',
    label: (
      <SharedTabLabel title="Shared" icon={<FolderSharedOutlinedIcon />} />
    )
  }
] as const satisfies readonly {
  value: SharedResourcesTab;
  label: React.ReactNode;
}[];

export const SharedResourceTabs: React.FC<SharedResourceTabsProps> = ({
  sharedResourceType,
  sharedTemplateType,
  renderMine,
  onImportShared,
  onModifyShared,
  onRevokeShared
}) => {
  const [tab, setTab] = useState<SharedResourcesTab>('mine');

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <AppTabs
        value={tab}
        onChange={setTab}
        tabs={sharedResourceTabs}
        appearance="minimal"
        sx={{ mb: 2.25 }}
      />

      <Box sx={{ minHeight: 0, flex: 1 }}>
        {tab === 'mine' ? (
          renderMine()
        ) : (
          <SharedResourcesGrid
            resourceType={sharedResourceType}
            templateType={sharedTemplateType}
            onImport={onImportShared}
            onModify={onModifyShared}
            onRevoke={onRevokeShared}
          />
        )}
      </Box>
    </Box>
  );
};
